import type { ZoneNet, ZoneScript } from '../game/ZoneRuntime';
import type { Actor, CastState } from '../sim/actor';
import type { HitResult } from '../sim/combat';
import type { SimEvent } from '../sim/events';
import { createMonster, createPlayer, resolvePlayerSkills } from '../sim/factory';
import type { PassiveRef } from '../sim/passives';
import { registry } from '../sim/registry';
import type { BaseStats } from '../sim/stats';
import type { StatusInstance } from '../sim/status';
import { type EntityId, type Team, isBossGrade } from '../sim/types';
import type { World } from '../sim/world';
import {
  MON_AIR,
  MON_ALIVE,
  MON_COUNTERED,
  MON_DOWN,
  MON_HAS_STAGGER,
  MON_STAGGER_ACTIVE,
  MONSTERS_PER_PACKET,
  MSG_MONSTERS,
  MSG_OWNER,
  type MonsterState,
  OWNER_AIR,
  OWNER_ALIVE,
  OWNER_DOWN,
  OWNER_INVULN,
  decodeMonsters,
  decodeOwner,
  encodeMonsters,
  encodeOwner,
} from './codec';
import { InterpBuffer, PeerClock, interpDelay } from './interp';
import type { JsonMessage, Room } from './transport';

/**
 * World replication for one shared zone (research 04 §4). Authority model:
 *
 * - Every player owns their character: movement, casts, statuses and HP.
 *   Owners stream a compact state packet and send reliable cast events.
 * - The zone host (oldest peer, or the party leader in instances) owns the
 *   monsters: spawns, AI, deaths and snapshots.
 * - Hits are resolved where they are felt (see `World.shouldResolve`) and
 *   forwarded to the target's owner as claims.
 *
 * Remote actors are rendered a little in the past (snapshot interpolation);
 * timed events from a peer (casts, deaths, statuses) are replayed on the same
 * delayed timeline so what a player sees stays consistent with where things
 * are — dodging is judged against exactly that picture.
 */

export const NET_VERSION = 1;

/** Everything other peers need to build a faithful copy of a player character. */
export interface PlayerProfile {
  name: string;
  classId: string;
  level: number;
  ilvl: number;
  base: BaseStats;
  bar: (string | null)[];
  awakening: string | null;
  skillLevels: Record<string, number>;
  tripods: Record<string, (string | null)[]>;
  skillMods: Record<string, { dmg?: number; cd?: number }>;
  passives: PassiveRef[];
  look: Actor['look'];
  team: Team;
  party: string | null;
  title: string | null;
  /** Guild tag shown on nameplates. */
  guild: string | null;
}

export interface PeerInfo {
  id: string;
  joinedAt: number;
  actorId: EntityId | null;
  profile: PlayerProfile | null;
  clock: PeerClock;
  /** Timed messages waiting for the delayed timeline. */
  queue: { lt: number; msg: JsonMessage }[];
  /** Round-trip time in ms (from pings). */
  rtt: number;
}

/** The parts of a loaded zone the replicator needs (satisfied by `ZoneRuntime`). */
export interface NetZone {
  readonly world: World;
  scripts: ZoneScript[];
  net: ZoneNet | null;
  localPlayerId: EntityId;
  /** Takes over another peer's monsters (host migration). */
  adoptMonsters(fromPeer: string): void;
  /** Removes the monsters this peer owns (yielding to an older host). */
  dropOwnedMonsters(): void;
}

export interface ReplicatorOptions {
  room: Room;
  zone: NetZone;
  /** Wall-clock join time (ms), used for host election. */
  joinedAt: number;
  /** Preferred host (the party leader in instances). */
  hostHint?: string | null;
  /** Snapshot rate for owner state and monsters (Hz). */
  rate?: number;
  /** Radius around each peer's character within which monsters are streamed to it. */
  interestRadius?: number;
  /** Extra profile fields that live outside the sim (item level, party, title). */
  profileExtra?: () => Partial<Pick<PlayerProfile, 'ilvl' | 'party' | 'title' | 'guild'>>;
  /** Local clock in seconds (injectable for tests). */
  clock?: () => number;
}

interface SpawnInfo {
  id: EntityId;
  d: string;
  l: number;
  x: number;
  z: number;
  f: number;
  tg: string[];
  tm: Team;
  mhp: number;
  hp: number;
  al: boolean;
  sm: number;
}

/** [id, remaining, duration, stacks, value, shield, sourceId, sourceAttack] */
type StatusWire = [string, number, number, number, number, number, number, number];

interface SentState {
  x: number;
  z: number;
  yaw: number;
  hp: number;
  flags: number;
  stagger: number;
  at: number;
}

type ReplicatorEvent =
  | { type: 'peerJoin'; peer: PeerInfo }
  | { type: 'peerLeave'; peer: PeerInfo }
  | { type: 'profile'; peer: PeerInfo }
  | { type: 'host'; host: string; self: boolean }
  | { type: 'version'; peer: string; version: number };

const TIMED = new Set(['cast', 'cend', 'death', 'rev', 'despawn', 'mstat', 'look', 'idm', 'fx']);
/** Events the owner forwards verbatim (peers cannot derive them). */
const FORWARDED = new Set<SimEvent['type']>(['counter', 'counterWindow', 'staggerBreak', 'partBreak', 'bossPhase']);
const KEEPALIVE = 1;
const PROFILE_CHECK = 1;
const STATUS_CHECK = 0.1;
const PING_INTERVAL = 2;
const SYNC_COOLDOWN = 3;
/** How long a preferred host may take to show up before the oldest peer takes over. */
const HINT_GRACE = 10;

const num = (v: unknown, d = 0): number => (typeof v === 'number' && Number.isFinite(v) ? v : d);
const str = (v: unknown, d = ''): string => (typeof v === 'string' ? v : d);

export class Replicator implements ZoneScript, ZoneNet {
  /** Peers that completed the handshake. */
  readonly peerMap = new Map<string, PeerInfo>();
  readonly room: Room;
  readonly zone: NetZone;
  readonly stats = { msgsOut: 0, msgsIn: 0, bytesOut: 0, bytesIn: 0 };
  private readonly world: World;
  private readonly rate: number;
  private readonly delay: number;
  private readonly interest: number;
  private readonly joinedAt: number;
  private hostHint: string | null;
  private readonly hintDeadline: number;
  private readonly clock: () => number;
  private buffers = new Map<EntityId, { buf: InterpBuffer; peer: string }>();
  private offs: (() => void)[] = [];
  private listeners = new Set<(e: ReplicatorEvent) => void>();
  private injected = new WeakSet<object>();
  private lastSent = new Map<string, Map<EntityId, SentState>>();
  private statusSig = new Map<EntityId, string>();
  private pendingSpawns: SpawnInfo[] = [];
  /** Monsters this peer hosts (their despawns are announced after the actor is gone). */
  private hosted = new Set<EntityId>();
  private lastProfile = '';
  private lastMode = '';
  private lastSnapshot = 0;
  private profileT = 0;
  private statusT = 0;
  private pingT = 0;
  private lastSyncReq = -Infinity;
  private disposed = false;

  constructor(private o: ReplicatorOptions) {
    this.room = o.room;
    this.zone = o.zone;
    this.world = o.zone.world;
    this.rate = o.rate ?? 15;
    this.delay = interpDelay(this.rate);
    this.interest = o.interestRadius ?? 70;
    this.joinedAt = o.joinedAt;
    this.hostHint = o.hostHint ?? null;
    this.clock = o.clock ?? (() => performance.now() / 1000);
    this.hintDeadline = this.clock() + HINT_GRACE;
    this.world.rebindLocalPeer(this.room.selfId);
    this.offs.push(
      this.room.onMessage((from, m) => this.onMessage(from, m)),
      this.room.onBinary((from, b) => this.onBinary(from, b)),
      this.room.onPeerJoin((id) => this.sendHello(id, false)),
      this.room.onPeerLeave((id) => this.onPeerLeave(id)),
    );
    this.zone.net = this;
    this.zone.scripts.unshift(this);
    this.trackHosted();
    for (const p of this.room.peers()) this.sendHello(p, false);
  }

  // ================================================================= public API

  get selfId(): string {
    return this.room.selfId;
  }

  get isHost(): boolean {
    return this.world.isHost;
  }

  on(cb: (e: ReplicatorEvent) => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  peerList(): PeerInfo[] {
    return [...this.peerMap.values()];
  }

  /** ZoneNet: script messages. */
  send(data: Record<string, unknown>, to?: string): void {
    this.out({ t: 'scr', d: data }, to);
  }

  /** ZoneNet: other peers in the zone. */
  peers(): string[] {
    return [...this.peerMap.keys()];
  }

  /** Local player actor. */
  private get me(): Actor | null {
    const id = this.zone.localPlayerId;
    return id >= 0 ? (this.world.actors.get(id) ?? null) : null;
  }

  private now(): number {
    return this.clock();
  }

  private nowMs(): number {
    return Math.round(this.clock() * 1000) >>> 0;
  }

  private emitEvent(e: ReplicatorEvent): void {
    for (const cb of [...this.listeners]) cb(e);
  }

  private out(msg: JsonMessage, to?: string | readonly string[]): void {
    if (this.disposed) return;
    this.stats.msgsOut++;
    this.room.send(msg, to);
  }

  private outBin(bytes: Uint8Array, to?: string | readonly string[]): void {
    if (this.disposed) return;
    this.stats.bytesOut += bytes.byteLength * (to === undefined ? this.peerMap.size : typeof to === 'string' ? 1 : to.length);
    this.room.sendBinary(bytes, to);
  }

  // ================================================================= profile / handshake

  private profileOf(a: Actor): PlayerProfile | null {
    const p = a.player;
    if (!p) return null;
    const extra = this.o.profileExtra?.() ?? {};
    return {
      name: a.name,
      classId: p.classId,
      level: p.level,
      ilvl: extra.ilvl ?? 0,
      base: { ...a.base },
      bar: [...p.bar],
      awakening: p.awakening,
      skillLevels: { ...p.skillLevels },
      tripods: structuredClone(p.tripods),
      skillMods: structuredClone(p.skillMods),
      passives: a.passives.map((r) => ({ id: r.id, level: r.level })),
      look: { ...a.look },
      team: a.team,
      party: extra.party ?? null,
      title: extra.title ?? null,
      guild: extra.guild ?? null,
    };
  }

  private sendHello(to: string, reply: boolean): void {
    const a = this.me;
    this.out(
      {
        t: 'hello',
        v: NET_VERSION,
        at: this.joinedAt,
        id: a?.id ?? null,
        p: a ? this.profileOf(a) : null,
        s: a ? { x: a.pos.x, z: a.pos.z, f: a.facing, hp: a.hpPct, al: a.alive } : null,
        host: this.world.hostPeer,
        reply,
      },
      to,
    );
  }

  private peerFor(id: string): PeerInfo {
    let p = this.peerMap.get(id);
    if (!p) {
      p = { id, joinedAt: Number.MAX_SAFE_INTEGER, actorId: null, profile: null, clock: new PeerClock(), queue: [], rtt: 0 };
      this.peerMap.set(id, p);
    }
    return p;
  }

  private onHello(from: string, m: JsonMessage): void {
    const v = num(m.v);
    if (v !== NET_VERSION) {
      this.emitEvent({ type: 'version', peer: from, version: v });
      return;
    }
    const isNew = !this.peerMap.has(from);
    const peer = this.peerFor(from);
    peer.joinedAt = num(m.at, Date.now());
    if (m.p && typeof m.id === 'number') this.upsertPlayer(peer, m.id, m.p as PlayerProfile, m.s as { x: number; z: number; f: number; hp: number; al: boolean } | null);
    if (isNew) {
      this.emitEvent({ type: 'peerJoin', peer });
      if (!m.reply) this.sendHello(from, true);
    }
    this.elect();
    if (isNew && this.world.isHost) {
      this.sendFullSpawns(from);
      for (const s of this.zone.scripts) if (s !== this) s.peerJoined?.(this.zone as never, from);
    }
  }

  private upsertPlayer(peer: PeerInfo, id: number, raw: PlayerProfile, st: { x: number; z: number; f: number; hp: number; al: boolean } | null): void {
    const prof = sanitizeProfile(raw);
    if (!prof) return;
    const world = this.world;
    let a = world.actors.get(id);
    if (a && (a.owner !== peer.id || a.kind !== 'player')) return; // id clash: ignore the newcomer
    if (peer.actorId !== null && peer.actorId !== id) {
      world.removeActor(peer.actorId);
      this.buffers.delete(peer.actorId);
    }
    peer.actorId = id;
    peer.profile = prof;
    if (!a) {
      a = createPlayer(world, {
        id,
        owner: peer.id,
        name: prof.name,
        classId: prof.classId,
        level: prof.level,
        pos: { x: num(st?.x), z: num(st?.z) },
        team: prof.team,
        base: prof.base,
        bar: prof.bar,
        awakening: prof.awakening,
        skillLevels: prof.skillLevels,
        tripods: prof.tripods,
        passives: prof.passives,
        skillMods: prof.skillMods,
        look: prof.look,
      });
      a.facing = num(st?.f);
      if (st) {
        a.hp = Math.max(1, Math.round(a.stats.maxHp * num(st.hp, 1)));
        if (!st.al) {
          a.alive = false;
          a.hp = 0;
        }
      }
    } else {
      const pl = a.player as NonNullable<Actor['player']>;
      a.name = prof.name;
      a.team = prof.team;
      pl.level = prof.level;
      pl.bar = prof.bar;
      pl.awakening = prof.awakening;
      pl.skillLevels = prof.skillLevels;
      pl.tripods = prof.tripods;
      pl.skillMods = prof.skillMods;
      a.passives = prof.passives;
      a.look = prof.look;
      a.setBase(prof.base);
      resolvePlayerSkills(a);
    }
    this.emitEvent({ type: 'profile', peer });
  }

  // ================================================================= host election

  /** Picks the host: the preferred peer if present (or still expected), else the oldest. */
  private electedHost(): string {
    const self = this.room.selfId;
    const hint = this.hostHint;
    if (hint) {
      if (hint === self || this.peerMap.has(hint)) return hint;
      if (this.now() < this.hintDeadline) return hint;
    }
    let best = { id: self, at: this.joinedAt };
    for (const p of this.peerMap.values()) {
      if (p.joinedAt < best.at || (p.joinedAt === best.at && p.id < best.id)) best = { id: p.id, at: p.joinedAt };
    }
    return best.id;
  }

  private elect(): void {
    const next = this.electedHost();
    const prev = this.world.hostPeer;
    if (next === prev) return;
    const self = this.room.selfId;
    const z = this.zone;
    if (next === self) {
      // Migration: take over the previous host's monsters where they stand.
      z.adoptMonsters(prev);
      this.world.hostPeer = self;
      this.trackHosted();
      this.sendFullSpawns();
    } else if (prev === self) {
      // An older host exists: drop our own copy of the zone population.
      this.hosted.clear();
      z.dropOwnedMonsters();
      this.world.hostPeer = next;
      this.requestSync(next, true);
    } else {
      for (const a of this.world.actors.values()) if (a.kind === 'monster' && a.owner === prev) a.owner = next;
      this.world.hostPeer = next;
    }
    this.lastSent.clear();
    for (const s of z.scripts) if (s !== this) s.hostChanged?.(z as never, next);
    this.emitEvent({ type: 'host', host: next, self: next === self });
  }

  private requestSync(host: string, force = false): void {
    const now = this.now();
    if (!force && now - this.lastSyncReq < SYNC_COOLDOWN) return;
    this.lastSyncReq = now;
    this.out({ t: 'sync' }, host);
  }

  private onPeerLeave(id: string): void {
    const peer = this.peerMap.get(id);
    if (!peer) return;
    this.peerMap.delete(id);
    this.lastSent.delete(id);
    if (peer.actorId !== null) {
      this.world.removeActor(peer.actorId);
      this.buffers.delete(peer.actorId);
    }
    for (const [eid, b] of this.buffers) if (b.peer === id) this.buffers.delete(eid);
    if (this.hostHint === id) this.hostHint = null;
    this.emitEvent({ type: 'peerLeave', peer });
    this.elect();
  }

  // ================================================================= monsters

  private spawnInfo(a: Actor): SpawnInfo | null {
    const m = a.monster;
    if (!m) return null;
    return { id: a.id, d: m.defId, l: m.level, x: a.pos.x, z: a.pos.z, f: a.facing, tg: [...m.tags], tm: a.team, mhp: a.stats.maxHp, hp: a.hp, al: a.alive, sm: m.stagger?.max ?? 0 };
  }

  private trackHosted(): void {
    this.hosted.clear();
    for (const a of this.world.actors.values()) if (a.kind === 'monster' && this.world.isLocal(a)) this.hosted.add(a.id);
  }

  private sendFullSpawns(to?: string): void {
    const list: SpawnInfo[] = [];
    for (const a of this.world.actors.values()) {
      if (a.kind !== 'monster' || !this.world.isLocal(a)) continue;
      const s = this.spawnInfo(a);
      if (s) list.push(s);
    }
    this.out({ t: 'spawn', list, full: true }, to);
  }

  private onSpawn(from: string, m: JsonMessage): void {
    if (from !== this.world.hostPeer || !Array.isArray(m.list)) return;
    const world = this.world;
    for (const raw of m.list as SpawnInfo[]) {
      const id = num(raw.id, -1);
      const def = str(raw.d);
      if (id <= 0 || !registry.monsters.has(def)) continue;
      let a = world.actors.get(id);
      if (a && a.kind !== 'monster') continue;
      if (!a) {
        a = createMonster(world, def, num(raw.l, 1), { x: num(raw.x), z: num(raw.z) }, { id, owner: from, facing: num(raw.f), tags: Array.isArray(raw.tg) ? raw.tg.map(String) : [], team: sanitizeTeam(raw.tm, 'monsters') });
      } else {
        a.owner = from;
      }
      const mhp = num(raw.mhp, a.stats.maxHp);
      if (mhp > 0 && mhp !== a.stats.maxHp) {
        a.base.maxHp = mhp;
        a.stats.maxHp = mhp;
        a.statsDirty = true;
      }
      a.hp = Math.min(mhp, num(raw.hp, mhp));
      const st = a.monster?.stagger;
      const sm = num(raw.sm);
      if (st && sm > 0) {
        st.max = sm;
        st.value = Math.min(st.value, sm);
      }
      if (!raw.al && a.alive) {
        a.alive = false;
        a.hp = 0;
        a.diedAt = world.time;
      }
    }
  }

  private flushSpawns(): void {
    if (this.pendingSpawns.length === 0) return;
    const list = this.pendingSpawns;
    this.pendingSpawns = [];
    if (this.peerMap.size) this.out({ t: 'spawn', list });
  }

  private monsterFlags(a: Actor): number {
    const m = a.monster;
    let f = 0;
    if (a.alive) f |= MON_ALIVE;
    if (m?.stagger) {
      f |= MON_HAS_STAGGER;
      if (m.stagger.active) f |= MON_STAGGER_ACTIVE;
    }
    if (m && m.countered > 0) f |= MON_COUNTERED;
    if (a.downTimer > 0) f |= MON_DOWN;
    if (a.airTimer > 0) f |= MON_AIR;
    return f;
  }

  /** Host: streams owned monsters near each peer's character, skipping unchanged ones. */
  private sendMonsterSnapshots(): void {
    const now = this.now();
    const time = this.nowMs();
    const owned: Actor[] = [];
    for (const a of this.world.actors.values()) if (a.kind === 'monster' && this.world.isLocal(a)) owned.push(a);
    if (owned.length === 0) return;
    const r2 = this.interest * this.interest;
    for (const peer of this.peerMap.values()) {
      const pa = peer.actorId !== null ? this.world.actors.get(peer.actorId) : undefined;
      if (!pa) continue;
      let sent = this.lastSent.get(peer.id);
      if (!sent) {
        sent = new Map();
        this.lastSent.set(peer.id, sent);
      }
      const list: MonsterState[] = [];
      for (const a of owned) {
        const dx = a.pos.x - pa.pos.x;
        const dz = a.pos.z - pa.pos.z;
        if (dx * dx + dz * dz > r2) continue;
        const flags = this.monsterFlags(a);
        const st = a.monster?.stagger;
        const stagger = st ? Math.round((st.value / Math.max(1, st.max)) * 255) : 0;
        const prev = sent.get(a.id);
        const changed =
          !prev ||
          Math.abs(prev.x - a.pos.x) > 0.02 ||
          Math.abs(prev.z - a.pos.z) > 0.02 ||
          Math.abs(prev.yaw - a.facing) > 0.02 ||
          prev.hp !== Math.round(a.hp) ||
          prev.flags !== flags ||
          prev.stagger !== stagger;
        if (!changed && (!a.alive || now - prev.at < KEEPALIVE)) continue;
        list.push({ id: a.id, x: a.pos.x, z: a.pos.z, yaw: a.facing, speed: a.speed, height: a.height, hp: a.hp, flags, stagger });
        sent.set(a.id, { x: a.pos.x, z: a.pos.z, yaw: a.facing, hp: Math.round(a.hp), flags, stagger, at: now });
      }
      for (let i = 0; i < list.length; i += MONSTERS_PER_PACKET) this.outBin(encodeMonsters(time, list.slice(i, i + MONSTERS_PER_PACKET)), peer.id);
    }
  }

  private applyMonsterState(a: Actor, s: MonsterState): void {
    const m = a.monster;
    if (!m) return;
    a.hp = Math.min(a.stats.maxHp, s.hp);
    if (m.stagger && s.flags & MON_HAS_STAGGER) {
      m.stagger.active = (s.flags & MON_STAGGER_ACTIVE) !== 0;
      m.stagger.value = (s.stagger / 255) * m.stagger.max;
    }
    m.countered = s.flags & MON_COUNTERED ? 0.25 : 0;
    a.downTimer = s.flags & MON_DOWN ? 0.25 : 0;
    a.airTimer = s.flags & MON_AIR ? 0.25 : 0;
  }

  // ================================================================= incoming

  private onBinary(from: string, b: Uint8Array): void {
    this.stats.bytesIn += b.byteLength;
    const peer = this.peerMap.get(from);
    if (!peer) return;
    const now = this.now();
    if (b[0] === MSG_OWNER) {
      const st = decodeOwner(b);
      if (!st || st.id !== peer.actorId) return;
      const a = this.world.actors.get(st.id);
      if (!a || a.owner !== from) return;
      const lt = peer.clock.observe(st.time, now);
      this.bufferFor(st.id, from).push(lt, { x: st.x, z: st.z, yaw: st.yaw, speed: st.speed, height: st.height });
      if (a.alive) a.hp = Math.max(1, Math.round(a.stats.maxHp * st.hpPct));
      a.mp = a.stats.maxMp * st.mpPct;
      a.downTimer = st.flags & OWNER_DOWN ? 0.25 : 0;
      a.airTimer = st.flags & OWNER_AIR ? 0.25 : 0;
      if (st.flags & OWNER_INVULN) a.invulnUntil = Math.max(a.invulnUntil, this.world.time + 0.25);
    } else if (b[0] === MSG_MONSTERS) {
      if (from !== this.world.hostPeer) return;
      const snap = decodeMonsters(b);
      if (!snap) return;
      const lt = peer.clock.observe(snap.time, now);
      let missing = false;
      for (const s of snap.list) {
        this.bufferFor(s.id, from).push(lt, { x: s.x, z: s.z, yaw: s.yaw, speed: s.speed, height: s.height });
        const a = this.world.actors.get(s.id);
        if (a) this.applyMonsterState(a, s);
        else if (s.flags & MON_ALIVE) missing = true;
      }
      if (missing) this.requestSync(from);
    }
  }

  private bufferFor(id: EntityId, peer: string): InterpBuffer {
    let e = this.buffers.get(id);
    if (!e || e.peer !== peer) {
      e = { buf: new InterpBuffer(), peer };
      this.buffers.set(id, e);
    }
    return e.buf;
  }

  private onMessage(from: string, m: JsonMessage): void {
    if (this.disposed || typeof m.t !== 'string') return;
    this.stats.msgsIn++;
    if (m.t === 'hello') {
      this.onHello(from, m);
      return;
    }
    const peer = this.peerMap.get(from);
    if (!peer) return; // no handshake yet
    if (TIMED.has(m.t)) {
      const tm = num(m.tm, -1);
      const now = this.now();
      const lt = tm >= 0 ? peer.clock.observe(tm, now) : now - this.delay;
      peer.queue.push({ lt, msg: m });
      return;
    }
    switch (m.t) {
      case 'prof':
        if (m.p && typeof m.id === 'number') this.upsertPlayer(peer, m.id, m.p as PlayerProfile, null);
        break;
      case 'claim':
        this.onClaim(from, m.r as HitResult);
        break;
      case 'hitfx':
        this.onHitFx(from, m.ev as SimEvent);
        break;
      case 'spawn':
        this.onSpawn(from, m);
        break;
      case 'sync':
        if (this.world.isHost) this.sendFullSpawns(from);
        break;
      case 'scr':
        if (m.d && typeof m.d === 'object') for (const s of this.zone.scripts) if (s !== this) s.message?.(this.zone as never, from, m.d as Record<string, unknown>);
        break;
      case 'ping':
        this.out({ t: 'pong', n: m.n ?? 0 }, from);
        break;
      case 'pong':
        if (typeof m.n === 'number') peer.rtt = Math.max(0, this.now() * 1000 - m.n);
        break;
      case 'bye':
        this.onPeerLeave(from);
        break;
      default:
        break;
    }
  }

  private onClaim(from: string, r: HitResult | undefined): void {
    if (!r || typeof r !== 'object') return;
    const t = this.world.actors.get(num(r.tgtId, -1));
    if (!t || !this.world.isLocal(t)) return;
    const src = this.world.actors.get(num(r.srcId, -1));
    if (src && src.owner !== from) return; // peers may only claim for actors they own
    if (!Number.isFinite(r.amount) || !Array.isArray(r.statuses)) return;
    this.world.applyHitResult(r);
  }

  private onHitFx(from: string, ev: SimEvent | undefined): void {
    if (!ev || ev.type !== 'hit') return;
    const t = this.world.actors.get(ev.tgtId);
    if (!t || t.owner !== from) return;
    const copy = { ...ev, predicted: false };
    this.injected.add(copy);
    this.world.emit(copy);
  }

  /** Replays due timed messages from each peer. */
  private processQueues(now: number): void {
    const rt = now - this.delay;
    for (const peer of this.peerMap.values()) {
      const q = peer.queue;
      let n = 0;
      while (n < q.length && (q[n] as { lt: number }).lt <= rt) n++;
      if (n === 0) continue;
      const due = q.splice(0, n);
      for (const { msg } of due) this.applyTimed(peer.id, msg);
    }
  }

  private applyTimed(from: string, m: JsonMessage): void {
    const world = this.world;
    const a = typeof m.a === 'number' ? world.actors.get(m.a) : typeof m.id === 'number' ? world.actors.get(m.id) : undefined;
    switch (m.t) {
      case 'cast': {
        if (!a || a.owner !== from || !a.alive) return;
        if (a.monster && (typeof m.tg === 'number' || m.tg === null)) a.monster.targetId = m.tg;
        world.replayCast(a, str(m.s), num(m.st), num(m.ang), { x: num(m.px), z: num(m.pz) }, num(m.c), (str(m.ph, 'stage') as CastState['phase']));
        const c = a.cast;
        if (c && c.castId === m.c && typeof m.m === 'number') {
          c.mult = m.m;
          c.areaScale = num(m.sc, 1);
        }
        return;
      }
      case 'cend': {
        const c = a?.cast;
        if (!a || a.owner !== from || !c || c.castId !== m.c) return;
        // Natural ends of an attack stage finish on their own timeline; releases and interrupts come from the owner.
        if (m.i || c.phase !== 'stage') world.endCast(a, !!m.i);
        return;
      }
      case 'death': {
        if (!a || a.owner !== from || !a.alive) return;
        const killer = typeof m.k === 'number' ? (world.actors.get(m.k) ?? null) : null;
        world.kill(a, killer);
        if (a.monster && isBossGrade(a.monster.grade)) this.clearAreasOf(a.id);
        return;
      }
      case 'rev': {
        if (!a || a.owner !== from) return;
        if (typeof m.x === 'number' && typeof m.z === 'number') {
          a.pos = { x: m.x, z: m.z };
          a.prevPos = { ...a.pos };
          this.buffers.get(a.id)?.buf.clear();
        }
        world.revive(a, num(m.hp, 1));
        return;
      }
      case 'despawn': {
        if (!a || a.owner !== from) return;
        world.removeActor(a.id);
        this.buffers.delete(a.id);
        this.statusSig.delete(a.id);
        return;
      }
      case 'mstat':
        if (a && a.owner === from) this.applyStatuses(a, m);
        return;
      case 'look':
        if (a && a.owner === from && m.look && typeof m.look === 'object') {
          const look = m.look as Actor['look'];
          if (typeof look.model === 'string') {
            a.look = { ...look };
            world.emit({ type: 'look', id: a.id, model: look.model });
          }
        }
        return;
      case 'idm':
        if (a?.player && a.owner === from) a.player.identity.mode = str(m.mode, 'idle');
        return;
      case 'fx':
        this.applyFx(from, m.ev as SimEvent | undefined);
        return;
      default:
        return;
    }
  }

  private applyFx(from: string, ev: SimEvent | undefined): void {
    if (!ev || typeof ev !== 'object' || typeof ev.type !== 'string') return;
    const world = this.world;
    switch (ev.type) {
      case 'counter': {
        const t = world.actors.get(ev.tgtId);
        if (!t || t.owner !== from) return;
        if (t.monster) t.monster.counterUntil = 0;
        break;
      }
      case 'counterWindow': {
        // Usually opened by the replayed cast already; scripts may also open one directly.
        const t = world.actors.get(ev.id);
        if (!t?.monster || t.owner !== from || t.monster.counterUntil > 0) return;
        world.openCounterWindow(t, ev.duration);
        return;
      }
      case 'staggerBreak':
      case 'partBreak':
      case 'bossPhase': {
        const t = world.actors.get(ev.id);
        if (!t || t.owner !== from) return;
        if (ev.type === 'partBreak' && t.monster) {
          const part = t.monster.parts.find((p) => p.id === ev.partId);
          if (part) {
            part.value = 0;
            part.broken = true;
          }
        }
        break;
      }
      case 'announce':
        if (from !== world.hostPeer) return;
        break;
      default:
        return;
    }
    const copy = { ...ev };
    this.injected.add(copy);
    world.emit(copy);
  }

  private clearAreasOf(ownerId: EntityId): void {
    for (const ar of this.world.areas) {
      if (ar.ownerId !== ownerId || ar.dead) continue;
      ar.dead = true;
      this.world.emit({ type: 'areaEnd', id: ar.id });
    }
  }

  // ================================================================= statuses

  private statusSignature(a: Actor): string {
    const t = this.world.time;
    let s = '';
    for (const st of a.statuses) s += `${st.id}:${st.stacks}:${Math.round(st.value * 1000)}:${Math.round(st.shield ?? 0)}:${Math.round((t + st.remaining) * 2)}|`;
    const parts = a.monster?.parts;
    if (parts?.length) for (const p of parts) s += `#${Math.round(p.value)}${p.broken ? 'x' : ''}`;
    return s;
  }

  private sendStatuses(a: Actor): void {
    const s: StatusWire[] = a.statuses.map((st) => [st.id, st.remaining, st.duration, st.stacks, st.value, st.shield ?? 0, st.sourceId, st.sourceAttack]);
    const msg: JsonMessage = { t: 'mstat', tm: this.nowMs(), id: a.id, s };
    const parts = a.monster?.parts;
    if (parts?.length) msg.pt = parts.map((p) => [p.value, p.broken ? 1 : 0]);
    this.out(msg);
  }

  private applyStatuses(a: Actor, m: JsonMessage): void {
    if (!Array.isArray(m.s)) return;
    const before = new Set(a.statuses.map((s) => s.id));
    const next: StatusInstance[] = [];
    for (const w of m.s as StatusWire[]) {
      if (!Array.isArray(w) || typeof w[0] !== 'string') continue;
      next.push({ id: w[0], remaining: num(w[1]), duration: num(w[2]), stacks: num(w[3], 1), value: num(w[4]), shield: num(w[5]) || undefined, sourceId: num(w[6]), sourceAttack: num(w[7]), tickTimer: 0 });
    }
    a.statuses = next;
    a.statsDirty = true;
    a.shield = next.reduce((sum, s) => sum + (s.shield ?? 0), 0);
    const after = new Set(next.map((s) => s.id));
    for (const id of after) if (!before.has(id)) this.world.emit({ type: 'status', tgtId: a.id, statusId: id, on: true });
    for (const id of before) if (!after.has(id)) this.world.emit({ type: 'status', tgtId: a.id, statusId: id, on: false });
    const parts = a.monster?.parts;
    if (parts && Array.isArray(m.pt)) {
      (m.pt as [number, number][]).forEach((p, i) => {
        const part = parts[i];
        if (!part || !Array.isArray(p)) return;
        part.value = num(p[0], part.value);
        part.broken = !!p[1];
      });
    }
  }

  /** Replica statuses keep counting down for display (the owner removes them). */
  private tickReplicaStatuses(dt: number): void {
    for (const a of this.world.aliveActors) {
      if (this.world.isLocal(a) || a.statuses.length === 0) continue;
      for (const s of a.statuses) s.remaining = Math.max(0, s.remaining - dt);
    }
  }

  // ================================================================= outgoing (owned actors)

  private checkLocalState(dt: number): void {
    const me = this.me;
    if (me?.player) {
      this.profileT -= dt;
      if (this.profileT <= 0) {
        this.profileT = PROFILE_CHECK;
        const prof = this.profileOf(me);
        if (prof) {
          const { look: _look, ...rest } = prof;
          const sig = JSON.stringify(rest);
          if (sig !== this.lastProfile) {
            this.lastProfile = sig;
            if (this.peerMap.size) this.out({ t: 'prof', id: me.id, p: prof });
          }
        }
      }
      const mode = me.player.identity.mode;
      if (mode !== this.lastMode) {
        this.lastMode = mode;
        if (this.peerMap.size) this.out({ t: 'idm', tm: this.nowMs(), id: me.id, mode });
      }
    }
    this.statusT -= dt;
    if (this.statusT <= 0) {
      this.statusT = STATUS_CHECK;
      for (const a of this.world.actors.values()) {
        if (a.kind === 'npc' || !this.world.isLocal(a)) continue;
        const sig = this.statusSignature(a);
        const prev = this.statusSig.get(a.id) ?? '';
        if (sig === prev) continue;
        this.statusSig.set(a.id, sig);
        if (this.peerMap.size) this.sendStatuses(a);
      }
    }
  }

  private sendOwnerState(): void {
    const a = this.me;
    if (!a || !this.peerMap.size) return;
    let flags = 0;
    if (a.alive) flags |= OWNER_ALIVE;
    if (a.downTimer > 0) flags |= OWNER_DOWN;
    if (a.airTimer > 0) flags |= OWNER_AIR;
    if (this.world.time < a.invulnUntil) flags |= OWNER_INVULN;
    this.outBin(encodeOwner({ id: a.id, time: this.nowMs(), x: a.pos.x, z: a.pos.z, yaw: a.facing, speed: a.speed, height: a.height, hpPct: a.hpPct, mpPct: a.stats.maxMp > 0 ? a.mp / a.stats.maxMp : 0, flags }));
  }

  private flushClaims(): void {
    for (const c of this.world.drainOutbox()) {
      if (c.toPeer === this.room.selfId || !this.peerMap.has(c.toPeer)) continue;
      this.out({ t: 'claim', r: c.result }, c.toPeer);
    }
  }

  // ================================================================= ZoneScript

  tick(_z: unknown, dt: number): void {
    if (this.disposed) return;
    const now = this.now();
    if (this.hostHint && !this.peerMap.has(this.hostHint) && this.hostHint !== this.room.selfId && now >= this.hintDeadline) this.elect();
    this.processQueues(now);
    this.applyInterpolation(now);
    this.tickReplicaStatuses(dt);
    this.flushClaims();
    this.checkLocalState(dt);
    this.flushSpawns();
    if (now - this.lastSnapshot >= 1 / this.rate) {
      this.lastSnapshot = now;
      this.sendOwnerState();
      if (this.world.isHost) this.sendMonsterSnapshots();
    }
    this.pingT -= dt;
    if (this.pingT <= 0 && this.peerMap.size) {
      this.pingT = PING_INTERVAL;
      this.out({ t: 'ping', n: now * 1000 });
    }
  }

  prerender(): void {
    if (!this.disposed) this.applyInterpolation(this.now());
  }

  /** Places remote actors at their interpolated position on the delayed timeline. */
  private applyInterpolation(now: number): void {
    const rt = now - this.delay;
    for (const [id, e] of this.buffers) {
      const a = this.world.actors.get(id);
      if (!a || this.world.isLocal(a)) continue;
      const s = e.buf.sample(rt);
      if (!s) continue;
      if (a.alive) {
        a.pos.x = s.x;
        a.pos.z = s.z;
        a.prevPos.x = s.x;
        a.prevPos.z = s.z;
        a.speed = s.speed;
      } else a.speed = 0;
      a.facing = s.yaw;
      // Channelled player skills turn with their owner (aim is owner-side input).
      if (a.player && a.cast && a.cast.phase !== 'casting') a.cast.aimAngle = s.yaw;
      a.height = s.height;
    }
  }

  event(_z: unknown, ev: SimEvent): void {
    if (this.disposed || this.injected.has(ev)) return;
    const world = this.world;
    const self = this.room.selfId;
    const owned = (id: EntityId): Actor | null => {
      const a = world.actors.get(id);
      return a && a.owner === self && a.kind !== 'npc' ? a : null;
    };
    switch (ev.type) {
      case 'castStage': {
        const a = owned(ev.actorId);
        if (!a || !this.peerMap.size) return;
        const c = a.cast;
        const msg: JsonMessage = { t: 'cast', tm: this.nowMs(), a: a.id, s: ev.skillId, st: ev.stage, ph: ev.phase, ang: ev.aimAngle, px: ev.aimPoint.x, pz: ev.aimPoint.z, c: ev.castId };
        if (a.monster) msg.tg = a.monster.targetId;
        if (c && c.castId === ev.castId && ev.phase === 'stage' && (c.mult !== 1 || c.areaScale !== 1)) {
          msg.m = c.mult;
          msg.sc = c.areaScale;
        }
        this.out(msg);
        return;
      }
      case 'castEnd': {
        if (owned(ev.actorId) && this.peerMap.size) this.out({ t: 'cend', tm: this.nowMs(), a: ev.actorId, c: ev.castId, i: ev.interrupted });
        return;
      }
      case 'spawn': {
        const a = owned(ev.id);
        if (a?.kind === 'monster') {
          this.hosted.add(a.id);
          const s = this.spawnInfo(a);
          if (s) this.pendingSpawns.push(s);
        }
        return;
      }
      case 'despawn': {
        // The actor is already gone; only monsters we hosted were ours to announce.
        this.statusSig.delete(ev.id);
        if (this.hosted.delete(ev.id) && this.peerMap.size) this.out({ t: 'despawn', tm: this.nowMs(), id: ev.id });
        return;
      }
      case 'death': {
        if (owned(ev.id) && this.peerMap.size) this.out({ t: 'death', tm: this.nowMs(), id: ev.id, k: ev.killerId });
        return;
      }
      case 'revive': {
        const a = owned(ev.id);
        if (a && this.peerMap.size) this.out({ t: 'rev', tm: this.nowMs(), id: a.id, hp: a.hpPct, x: a.pos.x, z: a.pos.z });
        return;
      }
      case 'look': {
        const a = owned(ev.id);
        if (a && this.peerMap.size) this.out({ t: 'look', tm: this.nowMs(), id: a.id, look: { ...a.look } });
        return;
      }
      case 'hit': {
        // Results on our own character caused by another player (PvP damage, heals, shields): show them to the source.
        if (ev.predicted || !this.peerMap.size) return;
        const tgt = owned(ev.tgtId);
        const src = world.actors.get(ev.srcId);
        if (tgt?.kind === 'player' && src?.kind === 'player' && src.owner !== self && this.peerMap.has(src.owner)) this.out({ t: 'hitfx', ev }, src.owner);
        return;
      }
      case 'announce': {
        // Script announcements (no casting actor) come from the host only.
        if (ev.actorId === undefined && world.isHost && this.peerMap.size) this.out({ t: 'fx', tm: this.nowMs(), ev });
        return;
      }
      default: {
        if (!FORWARDED.has(ev.type) || !this.peerMap.size) return;
        const id = 'id' in ev ? ev.id : 'tgtId' in ev ? ev.tgtId : null;
        if (typeof id === 'number' && owned(id)) this.out({ t: 'fx', tm: this.nowMs(), ev });
        return;
      }
    }
  }

  dispose(): void {
    if (this.disposed) return;
    this.out({ t: 'bye' });
    this.disposed = true;
    for (const off of this.offs) off();
    this.offs = [];
    if (this.zone.net === this) this.zone.net = null;
    this.zone.scripts = this.zone.scripts.filter((s) => s !== this);
    this.listeners.clear();
  }
}

const TEAMS: readonly Team[] = ['players', 'monsters', 'neutral', 'red', 'blue'];

function sanitizeTeam(v: unknown, d: Team): Team {
  return TEAMS.includes(v as Team) ? (v as Team) : d;
}

/** Validates a profile from the network; returns null if unusable. */
export function sanitizeProfile(p: PlayerProfile | null | undefined): PlayerProfile | null {
  if (!p || typeof p !== 'object') return null;
  const classId = str(p.classId);
  if (!registry.classes.has(classId)) return null;
  const level = Math.max(1, Math.min(70, Math.round(num(p.level, 1))));
  const base: Record<string, number> = {};
  if (p.base && typeof p.base === 'object') for (const [k, v] of Object.entries(p.base)) if (typeof v === 'number' && Number.isFinite(v)) base[k] = v;
  const strMap = (o: unknown): Record<string, number> => {
    const r: Record<string, number> = {};
    if (o && typeof o === 'object') for (const [k, v] of Object.entries(o)) if (typeof v === 'number' && Number.isFinite(v)) r[k] = v;
    return r;
  };
  const tripods: Record<string, (string | null)[]> = {};
  if (p.tripods && typeof p.tripods === 'object') for (const [k, v] of Object.entries(p.tripods)) if (Array.isArray(v)) tripods[k] = v.map((x) => (typeof x === 'string' ? x : null));
  const skillMods: Record<string, { dmg?: number; cd?: number }> = {};
  if (p.skillMods && typeof p.skillMods === 'object') for (const [k, v] of Object.entries(p.skillMods)) if (v && typeof v === 'object') skillMods[k] = { dmg: num((v as { dmg?: unknown }).dmg), cd: num((v as { cd?: unknown }).cd) };
  const look = p.look && typeof p.look === 'object' && typeof p.look.model === 'string' ? { ...p.look } : { model: registry.cls(classId).model };
  return {
    name: str(p.name, 'Adventurer').slice(0, 24),
    classId,
    level,
    ilvl: num(p.ilvl),
    base: { ...(base as unknown as BaseStats), level },
    bar: Array.isArray(p.bar) ? p.bar.slice(0, 8).map((x) => (typeof x === 'string' ? x : null)) : [],
    awakening: typeof p.awakening === 'string' ? p.awakening : null,
    skillLevels: strMap(p.skillLevels),
    tripods,
    skillMods,
    passives: Array.isArray(p.passives) ? p.passives.filter((r) => r && typeof r.id === 'string').map((r) => ({ id: r.id, level: num(r.level, 1) })) : [],
    look,
    team: sanitizeTeam(p.team, 'players'),
    party: typeof p.party === 'string' ? p.party : null,
    title: typeof p.title === 'string' ? p.title.slice(0, 32) : null,
    guild: typeof p.guild === 'string' ? p.guild.slice(0, 4) : null,
  };
}
